import React, { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, LayoutGrid, Columns3, MapPin, ArrowLeft, Users } from "lucide-react";

/* Helpers */
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfMonday = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const hm = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
// Iter46 fix: group bookings by LOCAL day, not UTC day. Using toISOString().slice(0,10)
// causes off-by-one-day bugs (e.g., Friday 19:15 Paris time → UTC 17:15 same day, but
// local-midnight Saturday in UTC is the same UTC day → booking displayed on Saturday).
const localDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const WEEKDAYS_FULL = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export default function PublicCalendar({ availabilities = [], pools = {}, onPick }) {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD or null
  const [prevView, setPrevView] = useState(null);

  const byDay = useMemo(() => {
    const m = {};
    availabilities.forEach((a) => {
      const d = new Date(a.start_dt);
      const key = localDayKey(d);
      (m[key] ||= []).push(a);
    });
    Object.values(m).forEach((list) => list.sort((a, b) => a.start_dt.localeCompare(b.start_dt)));
    return m;
  }, [availabilities]);

  const shift = (dir) => {
    const x = new Date(anchor);
    if (view === "week") x.setDate(x.getDate() + 7 * dir);
    else x.setMonth(x.getMonth() + dir);
    setAnchor(x);
  };

  const openDay = (dateKey) => {
    setPrevView(view);
    setSelectedDay(dateKey);
  };
  const backToCalendar = () => {
    setSelectedDay(null);
    if (prevView) setView(prevView);
  };

  const label = () => {
    if (view === "week") {
      const ws = startOfMonday(anchor);
      const we = addDays(ws, 6);
      const same = ws.getMonth() === we.getMonth();
      return same
        ? `Semaine du ${ws.getDate()} au ${we.getDate()} ${MONTHS[we.getMonth()]} ${we.getFullYear()}`
        : `Semaine du ${ws.getDate()} ${MONTHS[ws.getMonth()]} au ${we.getDate()} ${MONTHS[we.getMonth()]} ${we.getFullYear()}`;
    }
    return `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  };

  if (selectedDay) {
    return <DayView dateKey={selectedDay} byDay={byDay} pools={pools} onPick={onPick} onBack={backToCalendar} />;
  }

  return (
    <div className="card-soft overflow-hidden" data-testid="public-calendar">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-gradient-to-r from-sky-50 via-white to-cyan-50">
        <div className="inline-flex rounded-full border bg-white overflow-hidden" role="tablist">
          <button
            onClick={() => setView("week")} data-testid="cal-view-week"
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition ${view === "week" ? "bg-sky-700 text-white" : "text-slate-600 hover:bg-sky-50"}`}>
            <Columns3 className="w-3.5 h-3.5" />Semaine
          </button>
          <button
            onClick={() => setView("month")} data-testid="cal-view-month"
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition ${view === "month" ? "bg-sky-700 text-white" : "text-slate-600 hover:bg-sky-50"}`}>
            <LayoutGrid className="w-3.5 h-3.5" />Mois
          </button>
        </div>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" className="rounded-full capitalize font-medium text-slate-800" onClick={() => setAnchor(new Date())} data-testid="cal-today">
          <CalIcon className="w-3.5 h-3.5 mr-1" />{label()}
        </Button>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {view === "week" ? (
        <WeekView anchor={anchor} byDay={byDay} pools={pools} onPick={onPick} onOpenDay={openDay} />
      ) : (
        <MonthView anchor={anchor} byDay={byDay} pools={pools} onPick={onPick} onOpenDay={openDay} />
      )}
    </div>
  );
}

function Chip({ a, pools, onPick, layout = "default" }) {
  const full = a.booked_count >= a.max_students;
  const poolName = pools[a.pool_id]?.name || "";
  const base = full
    ? "bg-slate-100 text-slate-400 border-slate-200 line-through"
    : "bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-sm hover:shadow-md hover:-translate-y-0.5";
  return (
    <button
      disabled={full}
      onClick={(e) => { e.stopPropagation(); !full && onPick?.(a); }}
      data-testid={`cal-avail-${a.avail_id}`}
      data-chip="1"
      className={`block w-full text-left rounded-lg border px-2 py-1 text-[11px] transition ${base} ${layout === "compact" ? "" : "mb-1"}`}
      title={`${hm(a.start_dt)} → ${hm(a.end_dt)} • ${poolName} • ${a.booked_count}/${a.max_students}`}
    >
      <div className="font-semibold leading-tight">{hm(a.start_dt)} → {hm(a.end_dt)}</div>
      {layout !== "compact" && poolName && <div className="truncate opacity-90">{poolName}</div>}
      <div className="text-[10px] opacity-90">{full ? "Complet" : `${a.booked_count}/${a.max_students}`} {a.price ? `• ${a.price}€` : ""}</div>
    </button>
  );
}

function WeekView({ anchor, byDay, pools, onPick, onOpenDay }) {
  const ws = startOfMonday(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const today = new Date();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 p-3" data-testid="cal-week-grid">
      {days.map((d, i) => {
        const key = localDayKey(d);
        const slots = byDay[key] || [];
        const isToday = sameDay(d, today);
        const clickable = slots.length > 0;
        return (
          <div
            key={key} data-testid={`cal-day-${key}`}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : -1}
            onClick={(e) => {
              if (!clickable) return;
              // Ignore clicks that came from a chip (they handle their own onPick)
              if (e.target.closest("[data-chip]")) return;
              onOpenDay?.(key);
            }}
            onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpenDay?.(key); } }}
            className={`rounded-xl border p-2 min-h-[170px] transition ${isToday ? "border-sky-400 bg-sky-50/40" : "border-slate-200 bg-white"} ${clickable ? "hover:shadow-md hover:border-sky-300 cursor-pointer" : ""}`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <div className={`text-[11px] uppercase tracking-wider font-semibold ${isToday ? "text-sky-700" : "text-slate-500"}`}>{WEEKDAYS[i]}</div>
              <div className={`text-lg font-bold ${isToday ? "text-sky-700" : "text-slate-800"}`} style={{ fontFamily: "Outfit" }}>{d.getDate()}</div>
            </div>
            {slots.length === 0 ? (
              <div className="text-[10px] text-slate-300 text-center mt-4">—</div>
            ) : (
              <>
                <div>{slots.slice(0, 2).map((a) => <Chip key={a.avail_id} a={a} pools={pools} onPick={onPick} />)}</div>
                {slots.length > 2 && (
                  <div className="text-[11px] text-sky-700 font-medium pl-1 mt-1">+{slots.length - 2} autres →</div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, byDay, pools, onPick, onOpenDay }) {
  const first = startOfMonth(anchor);
  const gridStart = startOfMonday(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = new Date();
  return (
    <div className="p-3" data-testid="cal-month-grid">
      <div className="grid grid-cols-7 gap-2 mb-2">
        {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((d) => {
          const key = localDayKey(d);
          const slots = byDay[key] || [];
          const isCur = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const clickable = slots.length > 0;
          return (
            <div
              key={key} data-testid={`cal-day-${key}`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : -1}
              onClick={(e) => {
                if (!clickable) return;
                if (e.target.closest("[data-chip]")) return;
                onOpenDay?.(key);
              }}
              onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpenDay?.(key); } }}
              className={`rounded-lg border p-1.5 min-h-[96px] transition ${isCur ? "bg-white" : "bg-slate-50/50 opacity-70"} ${isToday ? "border-sky-400 ring-2 ring-sky-100" : "border-slate-200"} ${clickable ? "hover:shadow-md hover:border-sky-300 cursor-pointer" : ""}`}
            >
              <div className={`text-xs font-semibold mb-1 ${isToday ? "text-sky-700" : "text-slate-600"}`}>{d.getDate()}</div>
              {slots.slice(0, 2).map((a) => <Chip key={a.avail_id} a={a} pools={pools} onPick={onPick} layout="compact" />)}
              {slots.length > 2 && (
                <div className="text-[10px] text-sky-700 font-medium pl-1 mt-0.5">+{slots.length - 2} autres →</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-sky-500 to-cyan-500" />Disponible</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-200" />Complet</span>
        <span className="inline-flex items-center gap-1">💡 Cliquez sur une journée pour voir tous les créneaux</span>
      </div>
    </div>
  );
}

function DayView({ dateKey, byDay, pools, onPick, onBack }) {
  const slots = byDay[dateKey] || [];
  const date = new Date(dateKey + "T00:00:00");
  const label = `${WEEKDAYS_FULL[(date.getDay() + 6) % 7]} ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  return (
    <div className="card-soft overflow-hidden" data-testid="day-view">
      <div className="flex items-center gap-3 p-3 border-b bg-gradient-to-r from-sky-50 via-white to-cyan-50">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sky-700 font-medium hover:underline rounded-full px-3 py-1.5 bg-white border border-sky-200 shadow-sm"
          data-testid="day-view-back"
        >
          <ArrowLeft className="w-4 h-4" />Retour
        </button>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Créneaux disponibles</div>
          <div className="text-base font-semibold text-slate-900 capitalize" style={{ fontFamily: "Outfit" }}>{label}</div>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-xs bg-sky-100 text-sky-700 rounded-full px-3 py-1 font-semibold">
          {slots.length} {slots.length > 1 ? "créneaux" : "créneau"}
        </span>
      </div>
      {slots.length === 0 ? (
        <div className="p-8 text-center text-slate-500">Aucun créneau ce jour-là.</div>
      ) : (
        <div className="divide-y">
          {slots.map((a) => {
            const full = a.booked_count >= a.max_students;
            const pool = pools[a.pool_id];
            const duration = Math.round((new Date(a.end_dt) - new Date(a.start_dt)) / 60000);
            return (
              <div key={a.avail_id} className={`p-4 flex items-center gap-4 transition ${full ? "bg-slate-50/60 opacity-60" : "hover:bg-sky-50/60"}`} data-testid={`day-slot-${a.avail_id}`}>
                <div className="text-center shrink-0 w-20">
                  <div className="text-2xl font-bold text-sky-700 leading-tight" style={{ fontFamily: "Outfit" }}>{hm(a.start_dt)}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">→ {hm(a.end_dt)}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{duration} min</div>
                </div>
                <div className="flex-1 min-w-0">
                  {pool && (
                    <div className="flex items-center gap-1 text-sm text-slate-800 truncate">
                      <MapPin className="w-3.5 h-3.5 text-sky-700 shrink-0" />
                      <span className="font-semibold truncate">{pool.name}</span>
                    </div>
                  )}
                  {pool?.address && <div className="text-xs text-slate-500 truncate">{pool.address}</div>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {full ? "Complet" : `${a.booked_count}/${a.max_students} place${a.max_students > 1 ? "s" : ""}`}
                    </span>
                    {a.price > 0 && <span className="font-semibold text-slate-900">{a.price}€</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={full}
                  onClick={() => onPick?.(a)}
                  className="rounded-full bg-sky-700 hover:bg-sky-600 shrink-0"
                  data-testid={`day-book-${a.avail_id}`}
                >
                  {full ? "Complet" : "Réserver"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
